import { Config, IISMethods } from "../../config/IISMethods"
import { setProps } from "../../redux/actions"
import { store } from "../.."

/**
 * ErrorManager Class - Centralized error handling and reporting system
 * 
 * Features:
 * - Captures JavaScript errors, unhandled promise rejections, and resource loading failures
 * - Filters critical errors from noise using pattern matching
 * - Prevents duplicate error reporting with intelligent deduplication
 * - Batches errors for efficient API transmission
 * - Tracks error propagation to avoid cascading error reports
 * - Provides enhanced filename detection from stack traces
 */
class ErrorManager {
    constructor() {
        // Session tracking for error correlation
        this.sessionid = this.generateSessionid()

        // Deduplication mechanisms
        this.duplicateHashes = new Map() // Stores unique errors with occurrence count
        this.recentErrors = new Map() // Tracks recent errors to prevent immediate duplicates
        this.errorPropagationGroups = new Map() // Groups related errors to prevent cascading reports

        // State management
        this.isInitialized = false
        this.retryCount = 0
        this.isSending = false

        // Timing configurations
        this.duplicateTimeWindow = 1000 // 1 second window for duplicate detection
        this.errorPropagationWindow = 500 // 500ms window for related error grouping

        // Page change handling
        this.pageChangeTimeouts = new Set()

        // ========== CRITICAL ERROR PATTERNS ==========
        // These patterns identify errors that should always be captured and reported
        this.criticalErrorPatterns = [
            // Property access errors - most common JavaScript errors
            /TypeError: Cannot read properties of undefined \(reading/i,
            /TypeError: Cannot read properties of null \(reading/i,
            /Cannot read property '\w+' of undefined/i,
            /Cannot read property '\w+' of null/i,
            /cannot read properties of/i,
            /cannot read property/i,
            /undefined is not an object/i,
            /null is not an object/i,

            // Function call errors - when methods don't exist or are called incorrectly
            /TypeError: \w+ is not a function/i,
            /TypeError: Cannot read property '\w+' of undefined/i,
            /TypeError: undefined is not a function/i,
            /TypeError: null is not a function/i,
            /TypeError: Object doesn't support property or method/i,
            /is not a function/i,
            /is not a constructor/i,

            // Variable scope and reference errors
            /ReferenceError: \w+ is not defined/i,
            /ReferenceError: Cannot access/i,
            /Cannot access before initialization/i,
            /is not defined/i,
            /Identifier '\w+' has already been declared/i,

            // React-specific errors that affect component functionality
            /minified react error/i,
            /react-dom/i,
            /cannot update a component/i,
            /maximum update depth exceeded/i,
            /react hook/i,
            /hooks can only be called/i,
            /rendered more hooks than during the previous render/i,
            /rendered fewer hooks than expected/i,
            /Invalid hook call/i,
            /Expected the last optional argument to be a valid listener/i,

            // Memory and performance critical errors
            /maximum call stack/i,
            /stack overflow/i,
            /out of memory/i,
            /RangeError: Maximum call stack size exceeded/i,
            /RangeError: Invalid array length/i,
            /too much recursion/i,
            /memory leak/i,

            // Network and API communication errors
            /failed to fetch/i,
            /network error/i,
            /xhr error/i,
            /timeout/i,
            /cors/i,
            /ERR_NETWORK/i,
            /ERR_INTERNET_DISCONNECTED/i,
            /ERR_CONNECTION_REFUSED/i,
            /ERR_CONNECTION_TIMED_OUT/i,
            /fetch.*abort/i,
            /request timeout/i,
            /network request failed/i,

            // HTTP status code patterns for client and server errors
            /400|401|402|403|404|405|406|407|408|409|410|411|412|413|414|415|416|417|418|421|422|423|424|425|426|428|429|431|451/,
            /500|501|502|503|504|505|506|507|508|510|511/,
            /status.*40\d/i,
            /status.*50\d/i,
            /Internal Server Error/i,
            /Bad Gateway/i,
            /Service Unavailable/i,
            /Gateway Timeout/i,

            // Security and authentication errors
            /permission denied/i,
            /access denied/i,
            /unauthorized/i,
            /forbidden/i,
            /authentication/i,
            /login/i,
            /token.*expired/i,
            /invalid.*token/i,
            /csrf/i,
            /cross-site/i,

            // Business-critical functionality errors
            /payment/i,
            /checkout/i,
            /billing/i,
            /transaction/i,
            /order/i,
            /purchase/i,
            /subscription/i,
            /credit.*card/i,

            // Resource loading and module errors
            /failed to load/i,
            /script error/i,
            /loading chunk failed/i,
            /module not found/i,
            /ChunkLoadError/i,
            /Dynamic import.*failed/i,
            /Failed to import/i,
            /SyntaxError.*Unexpected token/i,
            /Error loading.*module/i,

            // Type conversion and syntax errors
            /TypeError: Assignment to constant variable/i,
            /TypeError: Invalid attempt to spread/i,
            /TypeError: Converting circular structure to JSON/i,
            /TypeError: Cannot convert undefined or null to object/i,
            /TypeError: Cannot create property/i,
            /TypeError: Cannot set property/i,
            /TypeError: Cannot delete property/i,
            /SyntaxError: Unexpected token/i,
            /SyntaxError: Unexpected end of JSON input/i,
            /SyntaxError: Invalid or unexpected token/i,

            // Array and object method errors
            /TypeError: Cannot use 'in' operator/i,
            /TypeError: \w+\.map is not a function/i,
            /TypeError: \w+\.filter is not a function/i,
            /TypeError: \w+\.reduce is not a function/i,
            /TypeError: \w+\.forEach is not a function/i,
            /Cannot read length of/i,
            /undefined.*length/i,

            // Async/Promise handling errors
            /UnhandledPromiseRejectionWarning/i,
            /Unhandled promise rejection/i,
            /Promise.*reject/i,
            /async.*await.*error/i,
            /Cannot resolve.*promise/i,

            // Database and data persistence errors
            /database.*error/i,
            /connection.*failed/i,
            /query.*failed/i,
            /transaction.*failed/i,
            /duplicate.*key/i,
            /constraint.*violation/i,
            /foreign.*key/i,

            // Form validation and user input errors
            /validation.*failed/i,
            /required.*field/i,
            /invalid.*email/i,
            /invalid.*format/i,
            /form.*error/i,

            // Media and file handling errors
            /failed.*upload/i,
            /file.*not.*found/i,
            /invalid.*file/i,
            /media.*error/i,
            /image.*load.*error/i,
            /video.*load.*error/i,
            /audio.*load.*error/i,

            // Mobile and device-specific errors
            /device.*not.*supported/i,
            /camera.*permission/i,
            /location.*permission/i,
            /notification.*permission/i,
            /storage.*permission/i,

            // General critical error patterns
            /fatal.*error/i,
            /critical.*error/i,
            /system.*error/i,
            /application.*error/i,
            /server.*error/i,
            /runtime.*error/i,
            /execution.*error/i,
            /processing.*error/i,
            /operation.*failed/i,
            /unable.*to.*complete/i,
            /something.*went.*wrong/i,
            /unexpected.*error/i,
            /internal.*error/i,
            /service.*unavailable/i,
            /temporarily.*unavailable/i,

            // Third-party library specific errors
            /Redux.*error/i,
            /Router.*error/i,
            /Axios.*error/i,
            /GraphQL.*error/i,
            /Apollo.*error/i,
            /Socket.*error/i,
            /WebSocket.*error/i,
            /PDF.*error/i,
            /Chart.*error/i,
            /Calendar.*error/i,
        ]

        // ========== IGNORED ERROR PATTERNS ==========
        // These patterns identify noise/non-critical errors that should be filtered out
        this.ignoredErrorPatterns = [
            /non-passive event listener/i, // Browser performance warnings
            /deprecated/i, // Deprecation warnings
            /warning/i, // General warnings
            /extension/i, // Browser extension related
            /chrome-extension/i,
            /moz-extension/i,
            /safari-extension/i,
            /adblock/i, // Ad blocker related
            /facebook/i, // Third-party tracking
            /google analytics/i,
            /gtag/i,
            /third-party/i,
            /ad blocker/i,
            /tracking/i,
            /analytics/i,
            /gtm/i, // Google Tag Manager
        ]
    }

    getCurrentState() {
        return store.getState()
    }

    /**
     * Initialize error handling system
     * Sets up all event listeners and handlers
     */
    init() {
        if (this.isInitialized) return

        this.setupErrorHandlers()
        this.setupPageChangeHandler()
        this.setupBeforeUnloadHandler()
        this.isInitialized = true
    }

    /**
     * Set up global error event listeners
     * Handles both JavaScript errors and resource loading failures
     */
    setupErrorHandlers() {
        // Global error handler for JavaScript errors and resource loading failures
        window.addEventListener('error', (event) => {
            if (event.target !== window && event.target.tagName) {
                // Resource loading error (images, scripts, stylesheets, etc.)
                const filename = this.getResourceFilename(event.target)
                this.captureError({
                    error: `Resource failed to load: ${event.target.src || event.target.href}`,
                    errorstack: '',
                    errortype: 'Network',
                    filename: filename,
                    linenumber: 0,
                    source: event.target.src || event.target.href || ''
                })
            } else {
                // JavaScript runtime error with enhanced filename detection
                const filename = this.getScriptFilename(event.filename, event.error?.stack)
                this.captureError({
                    error: event.error?.message || event.message,
                    errorstack: event.error?.stack || '',
                    errortype: 'JavaScript',
                    filename: filename,
                    linenumber: event.lineno || 0,
                    colno: event.colno || 0,
                    source: event.filename || ''
                })
            }
        }, true)

        // Unhandled promise rejection handler
        window.addEventListener('unhandledrejection', (event) => {
            const filename = this.getStackFilename(event.reason?.stack)
            this.captureError({
                error: event.reason?.message || String(event.reason),
                errorstack: event.reason?.stack || '',
                errortype: 'Promise',
                filename: filename,
                linenumber: 0,
                source: ''
            })
        })
    }

    // ========== FILENAME DETECTION METHODS ==========
    // These methods extract meaningful filenames from various error sources
    // Extract filename from failed resource elements (images, scripts, etc.)
    getResourceFilename(element) {
        const src = element.src || element.href || ''
        if (!src) return 'unknown'

        try {
            const url = new URL(src, window.location.origin)
            const pathname = url.pathname
            const filename = pathname.split('/').pop() || 'unknown'
            return filename.includes('.') ? filename : `${filename}.unknown`
        } catch {
            return 'unknown'
        }
    }

    /**
     * Get filename from JavaScript error events
     * Prioritizes event filename, falls back to stack trace analysis
     */
    getScriptFilename(eventFilename, stack) {
        // Try event filename first
        if (eventFilename && eventFilename !== 'undefined') {
            return this.extractFilenameFromPath(eventFilename)
        }

        // Try to get from stack trace
        if (stack) {
            return this.getStackFilename(stack)
        }

        return 'unknown.js'
    }

    /**
     * Extract meaningful filename from stack trace
     * Skips webpack internals and node_modules to find user code
     */
    getStackFilename(stack) {
        if (!stack) return 'unknown.js'

        const lines = stack.split('\n')
        for (const line of lines) {
            // Match common stack trace patterns: at function (file:line:col)
            const match = line.match(/(?:at|@)\s*(?:.*\s+\()?([^()]+):(\d+)(?::(\d+))?\)?/)
            if (match && match[1]) {
                const fullPath = match[1]
                // Skip webpack internal files, node_modules, browser extensions
                if (!fullPath.includes('webpack') &&
                    !fullPath.includes('node_modules') &&
                    !fullPath.includes('chrome-extension')) {
                    return this.extractFilenameFromPath(fullPath)
                }
            }
        }

        return 'unknown.js'
    }

    /**
     * Extract line number from stack trace for error location
     */
    getLineNumberFromStack(stack) {
        if (!stack) return 0

        const lines = stack.split('\n')
        for (const line of lines) {
            const match = line.match(/:(\d+)(?::(\d+))?/)
            if (match && match[1]) {
                return parseInt(match[1], 10)
            }
        }

        return 0
    }

    /**
     * Clean and extract filename from various path formats
     * Handles URLs, absolute paths, relative paths
     */
    extractFilenameFromPath(path) {
        if (!path) return 'unknown.js'

        // Remove query parameters and extract filename
        const cleanPath = path.split('?')[0]
        const parts = cleanPath.split('/')
        const filename = parts[parts.length - 1] || 'unknown.js'

        // Ensure filename has an extension
        return filename.includes('.') ? filename : `${filename}.js`
    }

    // ========== ERROR FILTERING METHODS ==========

    /**
     * Determine if an error should be captured based on criticality
     * Uses pattern matching against critical and ignored error lists
     */
    shouldCaptureError(errorMessage) {
        const message = errorMessage

        // First check if it should be ignored (browser warnings, extensions, etc.)
        for (const pattern of this.ignoredErrorPatterns) {
            if (pattern.test(message)) {
                return false
            }
        }

        // Then check if it matches critical error patterns
        for (const pattern of this.criticalErrorPatterns) {
            if (pattern.test(message)) {
                return true
            }
        }

        // Default: capture errors that seem important based on keywords
        return this.isImportantError(message)
    }

    /**
     * Check if error contains keywords that indicate user-impacting issues
     */
    isImportantError(message) {
        const importantKeywords = [
            'error', 'failed', 'cannot', 'undefined', 'null', 'exception',
            'invalid', 'missing', 'not found', 'denied', 'refused'
        ]

        return importantKeywords.some(keyword => message.includes(keyword))
    }

    /**
     * Identify critical warnings that should be treated as errors
     */
    isCriticalWarning(message) {
        const criticalWarnings = [
            /react/i,
            /performance/i,
            /deprecated.*will be removed/i,
            /security/i,
            /memory/i,
            /api/i,
            /authentication/i
        ]

        return criticalWarnings.some(pattern => pattern.test(message))
    }

    // ========== PAGE CHANGE HANDLING ==========

    /**
     * Set up page change detection for SPA routing
     * Monitors both popstate events and history API calls
     */
    setupPageChangeHandler() {
        // Clean up any existing timeouts
        this.pageChangeTimeouts.forEach(timeout => clearTimeout(timeout))
        this.pageChangeTimeouts.clear()

        // Listen for browser back/forward navigation
        window.addEventListener('popstate', () => this.handlePageChange())

        // Intercept programmatic navigation (pushState/replaceState)
        const originalPushState = window.history.pushState
        const originalReplaceState = window.history.replaceState

        window.history.pushState = (...args) => {
            originalPushState.apply(window.history, args)
            this.schedulePageChange()
        }

        window.history.replaceState = (...args) => {
            originalReplaceState.apply(window.history, args)
            this.schedulePageChange()
        }
    }

    /**
     * Schedule page change handling with debouncing
     * Prevents multiple rapid page changes from triggering multiple sends
     */
    schedulePageChange() {
        // Clear any pending timeouts
        this.pageChangeTimeouts.forEach(timeout => clearTimeout(timeout))
        this.pageChangeTimeouts.clear()

        // Schedule new page change handling
        const timeout = setTimeout(() => {
            this.handlePageChange()
            this.pageChangeTimeouts.delete(timeout)
        }, 100)

        this.pageChangeTimeouts.add(timeout)
    }

    /**
     * Set up handler for page unload events
     * Ensures errors are sent before user leaves the page
     */
    setupBeforeUnloadHandler() {
        window.addEventListener('beforeunload', () => {
            const currentBatch = this.getCurrentState().errorBatch
            if (currentBatch && currentBatch.length > 0) {
                this.sendBatch('page_unload')
            }
        })
    }

    /**
     * Handle page changes - send pending errors and reset tracking
     */
    handlePageChange() {
        // Send any pending errors before page change
        const currentBatch = this.getCurrentState().errorBatch
        if (currentBatch && currentBatch.length > 0) {
            this.sendBatch('page_change')
        }

        // Clear tracking maps for new page context
        this.duplicateHashes.clear()
        this.recentErrors.clear()
    }

    // ========== ERROR PROCESSING METHODS ==========

    /**
     * Generate unique session ID for error correlation
     */
    generateSessionid() {
        return Date.now().toString(36) + Math.random().toString(36).substr(2)
    }

    /**
     * Generate unique hash for error deduplication
     * Based on error message, type, and page location
     */
    async generateErrorHash(errorData) {
        const pagealias = window.location.pathname
        const errorMessage = errorData.error
        const hashString = `${errorMessage}-${errorData.errortype}-${pagealias}`

        try {
            const encoder = new TextEncoder()
            const data = encoder.encode(hashString)
            const hashBuffer = await crypto.subtle.digest('SHA-256', data)
            const hashArray = Array.from(new Uint8Array(hashBuffer))

            // Take first 6 bytes and convert to base36 (about 10 characters)
            return hashArray.slice(0, 6)
                .map(b => b.toString(36))
                .join('')
        } catch {
            // Fallback for browsers without crypto.subtle
            return btoa(hashString).substring(0, 10)
        }
    }

    /**
     * Generate key for grouping related errors
     * Normalizes error messages and groups by page
     */
    generatePropagationGroupKey(errorData) {
        // Replace numbers with X to group similar errors
        const errorMessage = errorData.error.replace(/\d+/g, 'X')
        const pagealias = window.location.pathname
        return `${errorMessage}-${pagealias}`
    }

    /**
     * Check if error is likely a propagation/cascade from another error
     * Prevents reporting multiple related errors that stem from the same root cause
     */
    isErrorPropagation(errorData) {
        const groupKey = this.generatePropagationGroupKey(errorData)
        const now = Date.now()

        if (this.errorPropagationGroups.has(groupKey)) {
            const lastErrorTime = this.errorPropagationGroups.get(groupKey)
            if (now - lastErrorTime < this.errorPropagationWindow) {
                // This is likely a propagated error from the same root cause
                return true
            }
        }

        // Record this error as potential source of future propagations
        this.errorPropagationGroups.set(groupKey, now)
        return false
    }

    /**
     * Check if error originates from React internal files
     * Used to prioritize user code errors over React internals
     */
    isReactInternalError(errorData) {
        const filename = errorData.filename || ''
        const stack = errorData.errorstack || ''

        const reactInternalPatterns = [
            /react-dom\.development\.js/i,
            /react-dom\.production\.js/i,
            /react\.development\.js/i,
            /react\.production\.js/i,
            /scheduler\.development\.js/i,
            /scheduler\.production\.js/i
        ]

        return reactInternalPatterns.some(pattern =>
            pattern.test(filename) || pattern.test(stack)
        )
    }

    /**
     * Prioritize user code errors over React internal errors
     * When multiple related errors occur, show the one from user code
     */
    prioritizeError(errors) {
        const userCodeError = errors.find(error => !this.isReactInternalError(error))
        const reactError = errors.find(error => this.isReactInternalError(error))

        if (userCodeError && reactError) {
            // Combine them - use user code as primary, add React error as context
            return {
                ...userCodeError,
                reactError: {
                    filename: reactError.filename,
                    linenumber: reactError.linenumber,
                    stack: reactError.errorstack
                },
                isPropagatedError: true
            }
        }

        return userCodeError || reactError || errors[0]
    }

    /**
     * Check if error was recently seen to prevent rapid duplicates
     */
    isRecentDuplicate(errorHash) {
        const now = Date.now()

        if (this.recentErrors.has(errorHash)) {
            const lastSeen = this.recentErrors.get(errorHash)
            if (now - lastSeen < this.duplicateTimeWindow) {
                return true
            }
        }

        this.recentErrors.set(errorHash, now)
        return false
    }

    /**
     * Detect browser type for error context
     */
    getBrowserInfo() {
        const ua = navigator.userAgent
        if (ua.includes('Chrome')) return 'Chrome'
        if (ua.includes('Firefox')) return 'Firefox'
        if (ua.includes('Safari')) return 'Safari'
        if (ua.includes('Edge')) return 'Edge'
        return 'Unknown'
    }

    /**
     * Main error capture method
     * Processes, filters, deduplicates, and batches errors for transmission
     */
    async captureError(errorData) {
        try {
            // Filter out non-critical errors early
            if (!this.shouldCaptureError(errorData.error)) {
                return
            }

            // Check if this is a cascaded/propagated error
            if (this.isErrorPropagation(errorData)) {
                return
            }

            // Generate unique hash for deduplication
            const errorHash = await this.generateErrorHash(errorData)

            // Skip if this exact error was just reported
            if (this.isRecentDuplicate(errorHash)) {
                return
            }

            // Check if this error already exists in our tracking
            if (this.duplicateHashes?.has(errorHash)) {
                const existingError = this.duplicateHashes.get(errorHash)
                existingError.count += 1
                existingError.time = new Date().toISOString()

                // Update with better filename info if available
                // Prefer user code filenames over React internals or unknowns
                if (errorData.filename && errorData.filename !== 'unknown.js') {
                    if (existingError.filename === 'unknown.js' ||
                        (this.isReactInternalError(existingError) && !this.isReactInternalError(errorData))) {
                        existingError.filename = errorData.filename
                        existingError.linenumber = errorData.linenumber
                        existingError.errorstack = errorData.errorstack
                    }
                }

                // Update the existing error in the batch
                const currentBatch = this.getCurrentState().errorBatch || []
                const existingIndex = currentBatch.findIndex(e => e.errorhash === errorHash)

                if (existingIndex !== -1) {
                    currentBatch[existingIndex] = existingError
                    await store.dispatch(setProps({ errorBatch: [...currentBatch] }))
                }

                return
            }

            // Create new error object for tracking and transmission
            const errorObject = {
                userid: IISMethods.getCookiesData("loginInfo")?.uid || 'anonymous',
                browser: this.getBrowserInfo(),
                platform: '2', // Web platform identifier
                subdomainname: IISMethods.getCookiesData("loginInfo")?.subdomainname || IISMethods.getSubdomainName(),
                url: window.location.href,
                pagealias: window.location.pathname,
                error: errorData.error,
                errorstack: errorData.errorstack,
                errortype: errorData.errortype,
                filename: errorData.filename || '',
                sessionid: this.sessionid,
                useraction: 'Critical Error',
                count: 1,
                errorhash: errorHash,
            }

            // Initialize error batch if it doesn't exist
            if (!this.getCurrentState().errorBatch) {
                await store.dispatch(setProps({ errorBatch: [] }))
            }

            // Add to batch and update tracking
            const updatedBatch = [...(this.getCurrentState().errorBatch || []), errorObject]
            await store.dispatch(setProps({ errorBatch: updatedBatch }))

            this.duplicateHashes.set(errorHash, errorObject)

            // Send batch if it reaches maximum size
            if (updatedBatch.length >= Config.errorConfig.maxBatchSize) {
                await this.sendBatch('batch_full')
            }
        } catch (error) {
            // Avoid infinite loops - don't capture errors from error handler
            console.warn('Error in captureError:', error)
        }
    }

    /**
     * Calculate error severity based on message content and type
     */
    calculateSeverity(errorMessage, errorType) {
        const message = errorMessage.toLowerCase()

        // High severity - business critical errors
        if (message.includes('payment') || message.includes('checkout') ||
            message.includes('authentication') || message.includes('unauthorized') ||
            message.includes('500') || message.includes('502') || message.includes('503') ||
            message.includes('maximum call stack') || message.includes('out of memory')) {
            return 'high'
        }

        // Medium severity - functional issues
        if (errorType === 'Network' || message.includes('api') ||
            message.includes('timeout') || message.includes('cors') ||
            message.includes('cannot read propert') || message.includes('is not a function')) {
            return 'medium'
        }

        // Low severity - minor issues
        return 'low'
    }

    async addData(reqData) {
        try {
            const url = Config.weburl + 'bulkerrorlogs'
            const useraction = 'addbulkerrorlogsdata'
            let { resBody } = await IISMethods.socketAddData(url, reqData, useraction, false)

            return {
                status: resBody?.status || resBody,
                response: resBody
            }
        } catch (error) {
            throw error
        }
    }

    async sendBatch(batchReason) {
        const currentBatch = this.getCurrentState().errorBatch

        // Nothing to send
        if (!currentBatch || currentBatch.length === 0) {
            return true
        }

        // Prevent concurrent sends
        if (this.isSending) {
            return false
        }

        this.isSending = true
        const batchToSend = [...currentBatch]
        const batchData = {
            data: batchToSend.map(error => ({
                ...error,
                batchReason // Track why this batch was sent
            })),
        }

        try {
            const result = await this.addData(batchData)

            if (result.status === 200 || result.status === '200') {
                // Success - clear the batch and reset tracking
                await store.dispatch(setProps({ errorBatch: [] }))
                this.retryCount = 0

                // Clean up duplicate tracking for sent errors
                batchToSend.forEach(error => {
                    if (error.errorhash) {
                        this.duplicateHashes.delete(error.errorhash)
                    }
                })

                return true
            } else {
                return false
            }
        } catch (error) {
            return false
        } finally {
            this.isSending = false
        }
    }

    /**
     * Clean up old tracking data to prevent memory leaks
     * Called periodically to remove stale entries
     */
    cleanupRecentErrors() {
        const now = Date.now()

        // Clean up recent errors older than 2x the duplicate window
        for (const [hash, timestamp] of this.recentErrors) {
            if (now - timestamp > this.duplicateTimeWindow * 2) {
                this.recentErrors.delete(hash)
            }
        }

        // Clean up propagation groups older than 4x the propagation window
        for (const [groupKey, timestamp] of this.errorPropagationGroups) {
            if (now - timestamp > this.errorPropagationWindow * 4) {
                this.errorPropagationGroups.delete(groupKey)
            }
        }
    }

    // ========== PUBLIC API METHODS ==========

    // Force send current error batch (for testing/debugging)
    async forceSendBatch() {
        await this.sendBatch('manual_force')
    }

    // Clear current error batch without sending (for testing/debugging)
    async clearBatch() {
        await store.dispatch(setProps({ errorBatch: [] }))
        this.duplicateHashes.clear()
    }

    // Clean up resources and event listeners
    cleanup() {
        this.pageChangeTimeouts.forEach(timeout => clearTimeout(timeout))
        this.pageChangeTimeouts.clear()
    }
}

// Create singleton instance for global error handling
const errorManager = new ErrorManager()

// Set up periodic cleanup to prevent memory leaks
// Runs every 30 seconds to clean old tracking data
setInterval(() => errorManager.cleanupRecentErrors(), 30000)

// Enhanced debugging methods for development and testing
// Available in browser console as window.ErrorManagerDebug
if (typeof window !== 'undefined') {
    window.ErrorManagerDebug = {
        // Force send current error batch
        forceSend: async () => await errorManager.forceSendBatch(),

        // Clear current error batch
        clearBatch: async () => await errorManager.clearBatch(),

        // Get current error batch for inspection
        getBatch: () => errorManager.getCurrentState().errorBatch || [],

        // Test critical error capture (will be reported)
        testCriticalError: () => {
            throw new Error("Test critical error: Cannot read property 'name' of null")
        },

        // Test ignored error (should not be captured)
        testIgnoredError: () => {
            console.error("Non-passive event listener warning - this should be ignored")
        },

        // Get list of critical error patterns
        getCriticalPatterns: () => errorManager.criticalErrorPatterns,

        // Get list of ignored error patterns
        getIgnoredPatterns: () => errorManager.ignoredErrorPatterns
    }
}

export const initErrorHandling = () => errorManager.init()
export const cleanupErrorHandling = () => errorManager.cleanup()
export { errorManager }